<script>
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing'; // Added for navigation
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';

  let users = [];
  let isLoading = true;
  let currentUser = null;
  let isSuperAdmin = false;

  authStore.subscribe(val => {
      currentUser = val.user;
      isSuperAdmin = val.isSuperAdmin;
  });

  onMount(async () => {
    const res = await authFetch(getApiUrl('/admin/users'));
    users = await res.json();
    isLoading = false;
  });

  async function updateBusinessStatus(user, status) {
      if(!confirm(`Change business status for ${user.displayName}?`)) return;
      await authFetch(getApiUrl('/admin/users/business-status'), {
          method: 'PUT',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ userId: user.userId, isBusiness: status })
      });
      user.isBusinessAccount = status;
      users = users;
  }

  async function updateRole(user, newRole) {
      if(!confirm(`Promote ${user.displayName} to ${newRole}?`)) return;
      
      const res = await authFetch(getApiUrl('/admin/users/role'), {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ 
              requesterId: currentUser.idTokenClaims.oid,
              targetUserId: user.userId, 
              newRole: newRole 
          })
      });
      
      if(res.ok) {
          user.role = newRole;
          users = users;
          alert("Role updated.");
      } else {
          alert("Failed to update role. Permission denied.");
      }
  }

  async function unbanUser(user) {
      if(!confirm($t('admin.users.unban_confirm'))) return;
      const res = await authFetch(getApiUrl(`/admin/users/${user.userId}/unban`), {
          method: 'POST'
      });
      if(res.ok) {
          user.promoBanUntil = null;
          user.rejectedSpamCount = 0;
          users = users;
          alert($t('admin.users.unbanned_success'));
      } else {
          alert("Failed to unban user.");
      }
  }
</script>

<div class="admin-container">
    <h2>User Management</h2>
    {#if isLoading}
        <p>Loading...</p>
    {:else}
        <table>
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Business</th>
                    <th>{$t('admin.users.ban_status')}</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {#each users as user}
                    <tr>
                        <td>
                          <!-- Added link to user details on the name -->
                          <Link to={`/admin/users/${user.userId}`}>{user.displayName}</Link>
                        </td>
                        <td>
                            <span class="role-badge {user.role || 'user'}">{user.role || 'user'}</span>
                        </td>
                        <td>{user.isBusinessAccount ? '✅' : '❌'}</td>
                        <td>
                            {#if user.promoBanUntil && new Date(user.promoBanUntil) > new Date()}
                                <span class="ban-badge danger" title={`Banned until ${new Date(user.promoBanUntil).toLocaleString()}`}>
                                    ⛔ {$t('admin.users.banned_until')} {new Date(user.promoBanUntil).toLocaleDateString()}
                                </span>
                            {:else}
                                <span class="clean-badge">{$t('admin.users.clean_status')}</span>
                            {/if}
                        </td>
                        <td>
                            <div class="btn-group">
                                <!-- Navigation Link -->
                                <Link to={`/admin/users/${user.userId}`} class="sm-btn link-btn">Details</Link>
                                
                                <button class="sm-btn" on:click={() => updateBusinessStatus(user, !user.isBusinessAccount)}>
                                    {user.isBusinessAccount ? 'Revoke Biz' : 'Grant Biz'}
                                </button>

                                {#if user.promoBanUntil && new Date(user.promoBanUntil) > new Date()}
                                    <button class="sm-btn warning" on:click={() => unbanUser(user)}>
                                        {$t('admin.users.unban')}
                                    </button>
                                {/if}
                                
                                {#if isSuperAdmin}
                                    {#if user.role === 'admin'}
                                        <button class="sm-btn danger" on:click={() => updateRole(user, 'user')}>Demote</button>
                                    {:else}
                                        <button class="sm-btn success" on:click={() => updateRole(user, 'admin')}>Make Admin</button>
                                    {/if}
                                {/if}
                            </div>
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    {/if}
</div>

<style>
    .admin-container { max-width: 1000px; margin: 2rem auto; padding: 1rem; background: white; border-radius: 8px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 1rem; border-bottom: 1px solid #eee; text-align: left; }
    .btn-group { display: flex; gap: 0.5rem; }
    .sm-btn { 
      padding: 0.4rem 0.8rem; border: 1px solid #ccc; background: #f8f9fa; 
      cursor: pointer; border-radius: 4px; font-size: 0.85rem; text-decoration: none; 
      color: inherit; display: inline-block;
    }
    .link-btn { background: #e7f3ff; border-color: #007bff; color: #007bff; }
    .sm-btn.success { background: #e2f0d9; border-color: #28a745; color: #28a745; }
    .sm-btn.danger { background: #ffe6e6; border-color: #dc3545; color: #dc3545; }
    .sm-btn.warning { background: #fff3cd; border-color: #ffeeba; color: #856404; font-weight: bold; }

    .ban-badge { background: #ffebe9; color: #cf222e; padding: 2px 8px; border-radius: 12px; font-size: 0.8rem; font-weight: bold; display: inline-block; }
    .clean-badge { color: #28a745; font-weight: 500; font-size: 0.85rem; }
    
    .role-badge { padding: 2px 8px; border-radius: 12px; font-size: 0.8rem; font-weight: bold; text-transform: uppercase; }
    .role-badge.super_admin { background: #6610f2; color: white; }
    .role-badge.admin { background: #007bff; color: white; }
    .role-badge.user { background: #e9ecef; color: #666; }
</style>